import { useEffect, useState } from "react";
import { api } from "../utils/api";
import { invalidateLookups } from "./useLookups";

const refreshCallbacks = new Set();

export const invalidateInitiatives = () =>
  refreshCallbacks.forEach((fn) => fn());

const describeError = (err, fallback) =>
  err.details?.length
    ? err.details.map((d) => d.message).join("; ")
    : err.message || fallback;

export const useInitiatives = (showArchived) => {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [writeError, setWriteError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const refresh = () => setTick((t) => t + 1);
    refreshCallbacks.add(refresh);
    return () => refreshCallbacks.delete(refresh);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(showArchived ? "/initiatives/all" : "/initiatives")
      .then(({ data }) => {
        if (!cancelled) setInitiatives(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load initiatives");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showArchived, tick]);

  const removeInitiativeLocally = (id) =>
    setInitiatives((prev) => prev.filter((item) => item.id !== id));

  /*
   * Re-fetches a single initiative after a stale-state conflict so the current
   * view reflects reality without refetching the whole list. Returns the fresh
   * data (or null if it's gone), so callers holding their own copy (e.g. an
   * open detail dialog) can resync too.
   */
  const refetchInitiative = async (id) => {
    try {
      const { data } = await api.get(`/initiatives/${id}`);
      setInitiatives((prev) =>
        !showArchived && data.archived
          ? prev.filter((item) => item.id !== id)
          : prev.map((item) => (item.id === id ? data : item)),
      );
      return data;
    } catch (err) {
      if (err.status === 404) removeInitiativeLocally(id);
      return null;
    }
  };

  const createInitiative = async (draft) => {
    setWriteError(null);
    try {
      const { data } = await api.post("/initiatives", draft);
      setInitiatives((prev) => [...prev, data]);
    } catch (err) {
      setWriteError(describeError(err, "Failed to create initiative"));
    }
  };

  /*
   * Attempts every item independently — one failure (e.g. a lookup value
   * deleted after the preview was built) doesn't stop the rest from being
   * attempted. Callers get back which items succeeded and which failed
   * (with why), rather than a single pass/fail signal for the whole batch.
   */
  const importInitiatives = async (items) => {
    setWriteError(null);
    const created = [];
    const failed = [];
    for (const item of items) {
      try {
        const { data } = await api.post("/initiatives", item);
        created.push(data);
      } catch (err) {
        failed.push({ item, message: describeError(err, "Failed to import") });
      }
    }
    if (created.length > 0) {
      setInitiatives((prev) => [...prev, ...created]);
    }
    return { created, failed };
  };

  const updateInitiative = async (id, patch) => {
    setWriteError(null);
    try {
      const { data } = await api.patch(`/initiatives/${id}`, patch);
      setInitiatives((prev) =>
        prev.map((item) => (item.id === id ? data : item)),
      );
    } catch (err) {
      setWriteError(describeError(err, "Failed to update initiative"));
      if (err.status === 404) removeInitiativeLocally(id);
      if (err.status === 422) invalidateLookups();
    }
  };

  const archiveInitiative = async (id) => {
    setWriteError(null);
    try {
      const { data } = await api.patch(`/initiatives/${id}/archive`);
      setInitiatives((prev) =>
        showArchived
          ? prev.map((item) => (item.id === id ? data : item))
          : prev.filter((item) => item.id !== id),
      );
      return data;
    } catch (err) {
      setWriteError(describeError(err, "Failed to archive initiative"));
      if (err.status === 409) return await refetchInitiative(id);
      if (err.status === 404) removeInitiativeLocally(id);
      return null;
    }
  };

  const bulkArchiveInitiatives = async (ids) => {
    setWriteError(null);
    try {
      const { data } = await api.patch("/initiatives/bulk-archive", { ids });
      const { archived } = data;
      const notFound = data.notFound.map(String);
      const alreadyArchived = data.alreadyArchived.map(String);
      const archivedIds = new Set(archived.map((item) => item.id));
      const droppedIds = new Set(
        showArchived ? notFound : [...notFound, ...alreadyArchived],
      );
      setInitiatives((prev) => {
        if (showArchived) {
          const byId = new Map(archived.map((item) => [item.id, item]));
          return prev
            .filter((item) => !droppedIds.has(item.id))
            .map((item) => byId.get(item.id) ?? item);
        }
        return prev.filter(
          (item) => !archivedIds.has(item.id) && !droppedIds.has(item.id),
        );
      });
      if (showArchived && alreadyArchived.length > 0) {
        alreadyArchived.forEach((id) => refetchInitiative(id));
      }
      return { archived, notFound, alreadyArchived };
    } catch (err) {
      setWriteError(describeError(err, "Failed to archive initiatives"));
      return null;
    }
  };

  const unarchiveInitiative = async (id) => {
    setWriteError(null);
    try {
      const { data } = await api.patch(`/initiatives/${id}/unarchive`);
      setInitiatives((prev) => {
        if (showArchived) {
          return prev.map((item) => (item.id === id ? data : item));
        }
        return [...prev, data].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
      });
      return data;
    } catch (err) {
      setWriteError(describeError(err, "Failed to unarchive initiative"));
      if (err.status === 409) return await refetchInitiative(id);
      if (err.status === 404) removeInitiativeLocally(id);
      return null;
    }
  };

  const deleteInitiative = async (id) => {
    setWriteError(null);
    try {
      await api.delete(`/initiatives/${id}`);
      removeInitiativeLocally(id);
    } catch (err) {
      setWriteError(describeError(err, "Failed to delete initiative"));
    }
  };

  const addComment = async (id, text) => {
    setWriteError(null);
    try {
      const { data } = await api.post(`/initiatives/${id}/comments`, { text });
      setInitiatives((prev) =>
        prev.map((item) => (item.id === id ? data : item)),
      );
      return data;
    } catch (err) {
      setWriteError(describeError(err, "Failed to add comment"));
      if (err.status === 404) return await refetchInitiative(id);
      return null;
    }
  };

  const editComment = async (id, commentId, text) => {
    setWriteError(null);
    try {
      const { data } = await api.patch(
        `/initiatives/${id}/comments/${commentId}`,
        { text },
      );
      setInitiatives((prev) =>
        prev.map((item) => (item.id === id ? data : item)),
      );
      return data;
    } catch (err) {
      setWriteError(describeError(err, "Failed to edit comment"));
      if (err.status === 404) return await refetchInitiative(id);
      return null;
    }
  };

  const deleteComment = async (id, commentId) => {
    setWriteError(null);
    try {
      const { data } = await api.delete(
        `/initiatives/${id}/comments/${commentId}`,
      );
      setInitiatives((prev) =>
        prev.map((item) => (item.id === id ? data : item)),
      );
      return data;
    } catch (err) {
      setWriteError(describeError(err, "Failed to delete comment"));
      if (err.status === 404) return await refetchInitiative(id);
      return null;
    }
  };

  const clearWriteError = () => setWriteError(null);

  return {
    initiatives,
    loading,
    error,
    writeError,
    clearWriteError,
    createInitiative,
    importInitiatives,
    updateInitiative,
    archiveInitiative,
    bulkArchiveInitiatives,
    unarchiveInitiative,
    deleteInitiative,
    addComment,
    editComment,
    deleteComment,
  };
};
